import { z } from 'zod';
import { pageSchema, timestampSchema } from './common';

/**
 * Cost monitoring (see `docs/cost-monitoring-requirements.md`, "Cost
 * foundation" phase -- docs/decisions.md carries the full list of scope
 * decisions).
 *
 * Two conventions govern this whole file:
 *
 *  1. **Money is an integer count of minor units, carried as a string.** Not a
 *     number: `Number` is a float, and a float is the wrong type for money at
 *     any size. A string of minor units plus an ISO 4217 code is exact, and the
 *     client formats it with `Intl.NumberFormat`. The server never sends a
 *     pre-formatted amount (requirements document 12.1).
 *  2. **A cost entry is a signed movement into exactly one ledger.** A balance
 *     is the sum of its movements; corrections are new negative movements,
 *     never edits. Every aggregate in this file is a sum, which is what makes
 *     the arithmetic checkable (requirements document 5.3).
 */

/**
 * A signed integer count of minor units. Bounded at 18 digits, which is the
 * widest value that survives a round trip through PostgreSQL's `bigint`.
 */
export const minorAmountSchema = z
  .string()
  .regex(/^-?(0|[1-9]\d{0,17})$/, 'must be an integer count of minor units');
export type MinorAmount = z.infer<typeof minorAmountSchema>;

/** ISO 4217, uppercase. Validated as a shape, not against a closed list. */
export const currencyCodeSchema = z.string().regex(/^[A-Z]{3}$/, 'must be an ISO 4217 code');

const isoDateSchema = z.string().date();

/** A reporting period: a calendar month in the project's timezone. */
export const periodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'must be YYYY-MM');
export type Period = z.infer<typeof periodSchema>;

/* -------------------------------------------------------------------------- */
/* Enumerations                                                                */
/* -------------------------------------------------------------------------- */

export const costAccountKindSchema = z.enum(['capex', 'opex', 'contingency', 'suspense']);
export type CostAccountKind = z.infer<typeof costAccountKindSchema>;

/**
 * The three ledgers answer three different questions and are never added
 * together (requirements document 5.4).
 */
export const costLedgerSchema = z.enum(['commitment', 'cost', 'cash']);
export type CostLedger = z.infer<typeof costLedgerSchema>;

export const costEntryTypeSchema = z.enum([
  // commitment
  'contract_award',
  'variation',
  'commitment_release',
  // cost
  'accrual',
  'invoice',
  'credit_note',
  'adjustment',
  // cash
  'payment',
  'refund',
]);
export type CostEntryType = z.infer<typeof costEntryTypeSchema>;

/**
 * Which ledger each entry type belongs to. The database enforces the same
 * pairing with a CHECK constraint; this is the copy the API and the browser
 * use, so a picker cannot offer an invalid combination in the first place.
 */
export const LEDGER_FOR_ENTRY_TYPE: Record<CostEntryType, CostLedger> = {
  contract_award: 'commitment',
  variation: 'commitment',
  commitment_release: 'commitment',
  accrual: 'cost',
  invoice: 'cost',
  credit_note: 'cost',
  adjustment: 'cost',
  payment: 'cash',
  refund: 'cash',
};

export const ENTRY_TYPES_BY_LEDGER: Record<CostLedger, CostEntryType[]> = {
  commitment: ['contract_award', 'variation', 'commitment_release'],
  cost: ['accrual', 'invoice', 'credit_note', 'adjustment'],
  cash: ['payment', 'refund'],
};

export const costBudgetKindSchema = z.enum(['original', 'revision']);
export type CostBudgetKind = z.infer<typeof costBudgetKindSchema>;

export const costBudgetStatusSchema = z.enum(['draft', 'approved', 'superseded']);
export type CostBudgetStatus = z.infer<typeof costBudgetStatusSchema>;

export const costPhasingMethodSchema = z.enum(['manual', 'curve', 'schedule']);
export type CostPhasingMethod = z.infer<typeof costPhasingMethodSchema>;

export const costCurveShapeSchema = z.enum(['linear', 's_curve', 'front_loaded', 'back_loaded']);
export type CostCurveShape = z.infer<typeof costCurveShapeSchema>;

export const costEntrySourceKindSchema = z.enum(['manual', 'import', 'integration']);
export type CostEntrySourceKind = z.infer<typeof costEntrySourceKindSchema>;

/**
 * How a source supplies data. `synced` and `live_detail` are declared here and
 * in the database enum but are not yet creatable through the API -- they belong
 * to the external-sources phase. Declaring them now costs nothing and means the
 * later migration adds behaviour rather than altering a type in use.
 */
export const costSourceModeSchema = z.enum(['internal', 'synced', 'live_detail']);
export type CostSourceMode = z.infer<typeof costSourceModeSchema>;

export const costForecastMethodSchema = z.enum([
  'manual',
  'budget_remaining',
  'run_rate',
  'committed_only',
]);
export type CostForecastMethod = z.infer<typeof costForecastMethodSchema>;

/** Whether reported amounts include recoverable value-added tax. */
export const vatTreatmentSchema = z.enum(['net', 'gross']);
export type VatTreatment = z.infer<typeof vatTreatmentSchema>;

export const costWarningSchema = z.object({
  code: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
  affectedAccountIds: z.array(z.string().uuid()),
});
export type CostWarning = z.infer<typeof costWarningSchema>;

/* -------------------------------------------------------------------------- */
/* Ownership and editability                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The four things that can independently have a master outside the dashboard:
 * the shape of the chart of accounts, the approved budget, the movements, and
 * the forecast (docs/cost-editing-design.md, section 4).
 */
export const costDomainSchema = z.enum(['structure', 'budget', 'entries', 'forecast']);
export type CostDomain = z.infer<typeof costDomainSchema>;

/**
 * Why a write is refused.
 *
 * Every value is an `ErrorCode`. That is deliberate: the reason a control is
 * disabled and the error returned if somebody posts anyway are the same string,
 * translated in one place, so the interface can never explain a refusal
 * differently from the API that makes it.
 */
export const costWriteBlockSchema = z.enum([
  'authz.permission_denied',
  'cost.source_owned',
  'cost.entry_source_owned',
  'cost.entry_voided',
  'cost.account_archived',
  'cost.account_not_posting',
  'cost.budget_version_immutable',
  'cost.forecast_version_immutable',
  'cost.period_closed',
]);
export type CostWriteBlock = z.infer<typeof costWriteBlockSchema>;

/**
 * One resolved answer to "may this be written".
 *
 * The API never sends an action it would refuse, so a client renders from this
 * and derives nothing (design section 2). `block` is null exactly when
 * `allowed` is true.
 */
export const costVerdictSchema = z.object({
  allowed: z.boolean(),
  block: costWriteBlockSchema.nullable(),
});
export type CostVerdict = z.infer<typeof costVerdictSchema>;

/** Who owns an account's data, resolved through the account's ancestry. */
export const costAuthoritySchema = z.object({
  sourceId: z.string().uuid(),
  sourceName: z.string(),
  mode: costSourceModeSchema,
  /** The domains this source claims over this account. */
  domains: z.array(costDomainSchema),
  /** The account the claim is attached to: this one, or an ancestor. */
  claimedAtAccountId: z.string().uuid(),
  lastSuccessAt: timestampSchema.nullable(),
  failing: z.boolean(),
});
export type CostAuthority = z.infer<typeof costAuthoritySchema>;

/** What may be done with an existing cost entry. */
export const costEntryActionSchema = z.enum(['update', 'void', 'reverse']);
export type CostEntryAction = z.infer<typeof costEntryActionSchema>;

/**
 * The verbs that do not vary per row, resolved once for the overview page.
 *
 * Per row would be noise -- these answer "is there anywhere in this project
 * this could be done", which is what a toolbar button needs to know. The
 * per-row answer travels with the row.
 */
export const costEditabilitySchema = z.object({
  /** Creating, renaming, moving or closing a cost account. */
  accounts: costVerdictSchema,
  /** Drafting a budget revision, and editing a draft's lines. */
  budget: costVerdictSchema,
  /** Approving the budget draft. */
  approval: costVerdictSchema,
  /** Drafting a forecast and editing its lines. */
  forecast: costVerdictSchema,
  /**
   * Issuing the forecast draft. Separate from `approval` because the two
   * lifecycles are separate: an approved budget with no open draft says nothing
   * about whether a forecast can be issued, and folding them together made the
   * forecast panel refuse an action the API would have taken.
   */
  forecastIssue: costVerdictSchema,
  /** Posting a manual entry anywhere in the project. */
  entries: costVerdictSchema,
  import: costVerdictSchema,
  settings: costVerdictSchema,
});
export type CostEditability = z.infer<typeof costEditabilitySchema>;

/* -------------------------------------------------------------------------- */
/* Project cost settings                                                       */
/* -------------------------------------------------------------------------- */

export const costSettingsSchema = z.object({
  projectId: z.string().uuid(),
  currency: currencyCodeSchema,
  vatTreatment: vatTreatmentSchema,
  forecastDefaultMethod: costForecastMethodSchema,
  forecastStalenessDays: z.number().int().min(1).max(365),
  version: z.number().int().nonnegative(),
});
export type CostSettings = z.infer<typeof costSettingsSchema>;

export const updateCostSettingsSchema = z
  .object({
    currency: currencyCodeSchema.optional(),
    vatTreatment: vatTreatmentSchema.optional(),
    forecastDefaultMethod: costForecastMethodSchema.optional(),
    forecastStalenessDays: z.number().int().min(1).max(365).optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateCostSettings = z.infer<typeof updateCostSettingsSchema>;

/* -------------------------------------------------------------------------- */
/* Cost account (the cost breakdown structure)                                 */
/* -------------------------------------------------------------------------- */

/**
 * Account codes are the key external systems map onto, so they permit the
 * punctuation real cost codes use rather than the stricter `resourceKeySchema`.
 */
export const costAccountCodeSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, 'must be alphanumeric with . _ -');

export const costAccountSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  /** Materialized path of ancestor ids, root first, excluding this row. */
  path: z.array(z.string().uuid()),
  depth: z.number().int().nonnegative(),
  orderIndex: z.number().int(),
  code: costAccountCodeSchema,
  name: z.string(),
  description: z.string().nullable(),
  kind: costAccountKindSchema,
  /** Only posting accounts accept entries; rollup nodes reject them (FR-002). */
  isPosting: z.boolean(),
  hasChildren: z.boolean(),
  archivedAt: timestampSchema.nullable(),
  /**
   * Null when this account's data is the dashboard's own -- which is every
   * account in a project that has configured no sources, so the common case
   * carries nothing.
   */
  authority: costAuthoritySchema.nullable(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type CostAccount = z.infer<typeof costAccountSchema>;

export const createCostAccountSchema = z
  .object({
    parentId: z.string().uuid().nullish(),
    code: costAccountCodeSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(4000).nullish(),
    kind: costAccountKindSchema.default('capex'),
    isPosting: z.boolean().default(true),
    orderIndex: z.number().int().min(0).max(9999).default(0),
  })
  .strict();
export type CreateCostAccount = z.infer<typeof createCostAccountSchema>;

export const updateCostAccountSchema = z
  .object({
    code: costAccountCodeSchema.optional(),
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(4000).nullish(),
    kind: costAccountKindSchema.optional(),
    isPosting: z.boolean().optional(),
    orderIndex: z.number().int().min(0).max(9999).optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateCostAccount = z.infer<typeof updateCostAccountSchema>;

export const moveCostAccountSchema = z
  .object({
    parentId: z.string().uuid().nullable(),
    orderIndex: z.number().int().min(0).max(9999).optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type MoveCostAccount = z.infer<typeof moveCostAccountSchema>;

/* -------------------------------------------------------------------------- */
/* Budget                                                                      */
/* -------------------------------------------------------------------------- */

/** Explicit per-period amounts, for `phasingMethod: manual`. */
export const costManualPhaseSchema = z
  .object({ period: periodSchema, amountMinor: minorAmountSchema })
  .strict();
export type CostManualPhase = z.infer<typeof costManualPhaseSchema>;

export const costBudgetLineSchema = z.object({
  id: z.string().uuid(),
  budgetVersionId: z.string().uuid(),
  costAccountId: z.string().uuid(),
  name: z.string().nullable(),
  amountMinor: minorAmountSchema,
  isContingency: z.boolean(),
  phasingMethod: costPhasingMethodSchema,
  curveShape: costCurveShapeSchema.nullable(),
  phaseStart: isoDateSchema.nullable(),
  phaseEnd: isoDateSchema.nullable(),
  scheduleTaskId: z.string().uuid().nullable(),
  supplierLabel: z.string().nullable(),
  notes: z.string().nullable(),
  /** The resolved per-period amounts, whatever method produced them. */
  phases: z.array(costManualPhaseSchema),
});
export type CostBudgetLine = z.infer<typeof costBudgetLineSchema>;

const budgetLineInput = {
  costAccountId: z.string().uuid(),
  name: z.string().max(200).nullish(),
  amountMinor: minorAmountSchema,
  isContingency: z.boolean().default(false),
  phasingMethod: costPhasingMethodSchema.default('curve'),
  curveShape: costCurveShapeSchema.nullish(),
  phaseStart: isoDateSchema.nullish(),
  phaseEnd: isoDateSchema.nullish(),
  scheduleTaskId: z.string().uuid().nullish(),
  supplierLabel: z.string().max(200).nullish(),
  notes: z.string().max(2000).nullish(),
  manualPhases: z.array(costManualPhaseSchema).max(600).optional(),
};

export const createCostBudgetLineSchema = z.object(budgetLineInput).strict();
export type CreateCostBudgetLine = z.infer<typeof createCostBudgetLineSchema>;

export const updateCostBudgetLineSchema = z
  .object({
    name: z.string().max(200).nullish(),
    amountMinor: minorAmountSchema.optional(),
    isContingency: z.boolean().optional(),
    phasingMethod: costPhasingMethodSchema.optional(),
    curveShape: costCurveShapeSchema.nullish(),
    phaseStart: isoDateSchema.nullish(),
    phaseEnd: isoDateSchema.nullish(),
    scheduleTaskId: z.string().uuid().nullish(),
    supplierLabel: z.string().max(200).nullish(),
    notes: z.string().max(2000).nullish(),
    manualPhases: z.array(costManualPhaseSchema).max(600).optional(),
  })
  .strict();
export type UpdateCostBudgetLine = z.infer<typeof updateCostBudgetLineSchema>;

export const costBudgetVersionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  sequence: z.number().int().positive(),
  label: z.string(),
  kind: costBudgetKindSchema,
  status: costBudgetStatusSchema,
  reason: z.string().nullable(),
  totalMinor: minorAmountSchema,
  contingencyMinor: minorAmountSchema,
  lineCount: z.number().int().nonnegative(),
  approvedByUserId: z.string().uuid().nullable(),
  approvedAt: timestampSchema.nullable(),
  effectiveFrom: isoDateSchema.nullable(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type CostBudgetVersion = z.infer<typeof costBudgetVersionSchema>;

export const createCostBudgetVersionSchema = z
  .object({
    label: z.string().min(1).max(200),
    reason: z.string().max(2000).nullish(),
    /**
     * Copy the current approved version's lines into the new draft. A revision
     * is a complete statement of the budget, not a delta, so starting from the
     * current one is almost always what the author means (requirements
     * document 5.2).
     */
    copyFromCurrent: z.boolean().default(true),
  })
  .strict();
export type CreateCostBudgetVersion = z.infer<typeof createCostBudgetVersionSchema>;

export const approveCostBudgetVersionSchema = z
  .object({
    /** Required on a revision; a first budget has nothing to explain (FR-007). */
    reason: z.string().max(2000).nullish(),
    effectiveFrom: isoDateSchema.nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type ApproveCostBudgetVersion = z.infer<typeof approveCostBudgetVersionSchema>;

/* -------------------------------------------------------------------------- */
/* Cost entry                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * How a cost entry's document is reachable, if at all: a plain label with
 * nothing to open (`text`), an external link (`url`), or an uploaded file
 * (`file`, stored through the `Asset` system and referenced by
 * `/api/v1/assets/<id>` -- the same relative-URL shape a Markdown image
 * embed already uses, so `AssetsService.claimInTransaction` needs no new
 * code to pick it up).
 */
export const costDocumentKindSchema = z.enum(['text', 'url', 'file']);
export type CostDocumentKind = z.infer<typeof costDocumentKindSchema>;

const ASSET_URL_PATTERN =
  /^\/api\/v1\/assets\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The three-mode invariant `documentKind`/`documentRef`/`documentUrl` must
 * hold, shared by create and update so the two schemas cannot drift apart:
 *
 *  - no kind: nothing else may be set.
 *  - `text`: a label and nothing else.
 *  - `url`: an absolute `http(s)` URL; the label is an optional caption.
 *  - `file`: the URL must be one this server issued
 *    (`/api/v1/assets/<uuid>`), never an arbitrary string; the label is an
 *    optional caption, defaulted client-side from the upload's filename.
 *
 * Mirrored by a database `CHECK` constraint, the same belt-and-braces
 * pairing this file already uses for `ledger`/`entryType`.
 */
function checkDocumentFields(
  data: {
    documentKind?: CostDocumentKind | null;
    documentRef?: string | null;
    documentUrl?: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  const kind = data.documentKind ?? null;
  const ref = data.documentRef ?? null;
  const url = data.documentUrl ?? null;

  if (kind === null || kind === undefined) {
    if (ref !== null || url !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['documentKind'],
        message: 'documentRef/documentUrl require a documentKind',
      });
    }
    return;
  }

  if (kind === 'text') {
    if (!ref) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['documentRef'], message: 'required' });
    }
    if (url !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['documentUrl'],
        message: 'must be empty',
      });
    }
    return;
  }

  if (!url) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['documentUrl'], message: 'required' });
    return;
  }

  if (kind === 'url' && !/^https?:\/\//i.test(url)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['documentUrl'],
      message: 'must be an absolute http(s) URL',
    });
  }

  if (kind === 'file' && !ASSET_URL_PATTERN.test(url)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['documentUrl'],
      message: 'must be an uploaded asset reference',
    });
  }
}

export const costEntrySchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  costAccountId: z.string().uuid(),
  ledger: costLedgerSchema,
  entryType: costEntryTypeSchema,
  /** Signed, in the project currency, frozen at posting. */
  amountMinor: minorAmountSchema,
  sourceCurrency: currencyCodeSchema.nullable(),
  sourceAmountMinor: minorAmountSchema.nullable(),
  fxRate: z.string().nullable(),
  fxRateDate: isoDateSchema.nullable(),
  taxAmountMinor: minorAmountSchema.nullable(),
  effectiveOn: isoDateSchema,
  documentDate: isoDateSchema.nullable(),
  documentKind: costDocumentKindSchema.nullable(),
  documentRef: z.string().nullable(),
  documentUrl: z.string().nullable(),
  documentGroupRef: z.string().nullable(),
  /**
   * What the *source* said. Provenance, kept even when the attribution below
   * disagrees with it (contracts: commercial.ts).
   */
  supplierLabel: z.string().nullable(),
  contractRef: z.string().nullable(),
  /**
   * What the project decided those strings mean. Ids rather than embedded
   * records: the entry list is paged and hot, and every client that renders one
   * has already fetched the party and contract lists for its own pickers.
   */
  partyId: z.string().uuid().nullable(),
  contractId: z.string().uuid().nullable(),
  scheduleTaskId: z.string().uuid().nullable(),
  description: z.string().nullable(),
  tags: z.array(z.string()),
  reversesEntryId: z.string().uuid().nullable(),
  sourceKind: costEntrySourceKindSchema,
  costSourceId: z.string().uuid().nullable(),
  externalRef: z.string().nullable(),
  voidedAt: timestampSchema.nullable(),
  voidReason: z.string().nullable(),
  /**
   * What this caller may do with this row, resolved by the server.
   *
   * Roughly thirty bytes on a fifty-row page, and it removes the last place
   * where the browser could reach a different conclusion than the API: the
   * alternative is shipping the source kind, the account's claim and the
   * caller's permissions and combining them again in JSX.
   */
  actions: z.array(costEntryActionSchema),
  /** Why the list is short. Null when every action is available. */
  blockedBy: costWriteBlockSchema.nullable(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type CostEntry = z.infer<typeof costEntrySchema>;

/**
 * The ledger is derived from the entry type rather than accepted from the
 * client. Two fields that must agree are two fields that can disagree; the
 * client sends the one that carries the meaning.
 */
export const createCostEntrySchema = z
  .object({
    costAccountId: z.string().uuid(),
    entryType: costEntryTypeSchema,
    amountMinor: minorAmountSchema,
    effectiveOn: isoDateSchema,
    sourceCurrency: currencyCodeSchema.nullish(),
    sourceAmountMinor: minorAmountSchema.nullish(),
    fxRate: z
      .string()
      .regex(/^\d+(\.\d{1,10})?$/)
      .nullish(),
    fxRateDate: isoDateSchema.nullish(),
    taxAmountMinor: minorAmountSchema.nullish(),
    documentDate: isoDateSchema.nullish(),
    documentKind: costDocumentKindSchema.nullish(),
    documentRef: z.string().max(120).nullish(),
    documentUrl: z.string().max(2048).nullish(),
    documentGroupRef: z.string().max(120).nullish(),
    supplierLabel: z.string().max(200).nullish(),
    contractRef: z.string().max(120).nullish(),
    partyId: z.string().uuid().nullish(),
    contractId: z.string().uuid().nullish(),
    scheduleTaskId: z.string().uuid().nullish(),
    description: z.string().max(2000).nullish(),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    reversesEntryId: z.string().uuid().nullish(),
  })
  .strict()
  .superRefine(checkDocumentFields);
export type CreateCostEntry = z.infer<typeof createCostEntrySchema>;

/**
 * Only the descriptive fields are editable, and only on a manually authored
 * entry. The amount, the account, the ledger and the date are the record of
 * what happened: changing one silently restates a published report, which is
 * exactly what the signed-movement model exists to prevent. Correcting a
 * posted amount means voiding and re-posting, or posting an adjustment.
 */
export const updateCostEntrySchema = z
  .object({
    documentKind: costDocumentKindSchema.nullish(),
    documentRef: z.string().max(120).nullish(),
    documentUrl: z.string().max(2048).nullish(),
    documentGroupRef: z.string().max(120).nullish(),
    supplierLabel: z.string().max(200).nullish(),
    contractRef: z.string().max(120).nullish(),
    /* Attribution is descriptive, so unlike the amount, the account, the ledger
       and the date it stays editable on a posted entry: deciding afterwards
       which contract an invoice belonged to does not restate what happened. */
    partyId: z.string().uuid().nullish(),
    contractId: z.string().uuid().nullish(),
    scheduleTaskId: z.string().uuid().nullish(),
    description: z.string().max(2000).nullish(),
    tags: z.array(z.string().min(1).max(40)).max(20).optional(),
    version: z.number().int().nonnegative(),
  })
  .strict()
  .superRefine((data, ctx) => {
    // The document fields travel as a set: a patch that touches any one of
    // them must leave the triple consistent, so any absent member is read
    // back from `current` before the shared check runs. A patch that
    // touches none of them skips the check entirely -- every other field on
    // this schema is independently optional, and the document trio should
    // be no different.
    if (
      data.documentKind === undefined &&
      data.documentRef === undefined &&
      data.documentUrl === undefined
    ) {
      return;
    }
    checkDocumentFields(data, ctx);
  });
export type UpdateCostEntry = z.infer<typeof updateCostEntrySchema>;

export const voidCostEntrySchema = z
  .object({
    reason: z.string().min(1).max(500),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type VoidCostEntry = z.infer<typeof voidCostEntrySchema>;

export const costEntryListQuerySchema = z.object({
  accountId: z.string().uuid().optional(),
  /** Include descendants of `accountId`. Defaults to true: a drill-down means the subtree. */
  includeDescendants: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  ledger: costLedgerSchema.optional(),
  entryType: costEntryTypeSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  supplier: z.string().max(200).optional(),
  documentGroupRef: z.string().max(120).optional(),
  includeVoided: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type CostEntryListQuery = z.infer<typeof costEntryListQuerySchema>;

export const costEntryPageSchema = pageSchema(costEntrySchema);

/**
 * `documentGroupRef` is a correlation key a user types by convention (see
 * the field's own doc comment on `CostEntry`), not a managed entity -- so
 * this is a suggest-as-you-type lookup over values already used in the
 * project, not a directory. `q` narrows; leaving it out returns whatever
 * exists, most recently... alphabetically, since "recent" has no meaning
 * for a value with no timestamp of its own.
 */
export const documentGroupSuggestQuerySchema = z.object({
  q: z.string().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type DocumentGroupSuggestQuery = z.infer<typeof documentGroupSuggestQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Import                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * CSV arrives as text in a JSON body rather than as a multipart upload. Not
 * because this application refuses file uploads generally -- it doesn't, see
 * the `Asset` system a single cost entry's document now uses -- but because a
 * bulk import isn't the shape of one attached document: a bounded string
 * needs no temporary files, no content-type sniffing and no virus scanning,
 * and the paste-a-spreadsheet-column flow is what cost engineers actually do.
 */
export const costImportSchema = z
  .object({
    sourceName: z.string().min(1).max(120),
    csv: z.string().min(1).max(2_000_000),
    /** When false the response reports what would happen and writes nothing. */
    commit: z.boolean().default(false),
  })
  .strict();
export type CostImport = z.infer<typeof costImportSchema>;

export const costImportRowErrorSchema = z.object({
  row: z.number().int().positive(),
  code: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});

export const costImportResultSchema = z.object({
  committed: z.boolean(),
  sourceId: z.string().uuid().nullable(),
  rowsRead: z.number().int().nonnegative(),
  rowsAccepted: z.number().int().nonnegative(),
  rowsRejected: z.number().int().nonnegative(),
  /** Dry-run totals, so the operator sees the effect before agreeing to it. */
  totalsByLedger: z.record(costLedgerSchema, minorAmountSchema),
  errors: z.array(costImportRowErrorSchema).max(200),
});
export type CostImportResult = z.infer<typeof costImportResultSchema>;

/* -------------------------------------------------------------------------- */
/* Analytics                                                                   */
/* -------------------------------------------------------------------------- */

export const costFreshnessSchema = z.object({
  sourceId: z.string().uuid().nullable(),
  name: z.string(),
  mode: costSourceModeSchema,
  lastSuccessAt: timestampSchema.nullable(),
  failing: z.boolean(),
});
export type CostFreshness = z.infer<typeof costFreshnessSchema>;

export const costForecastStateSchema = z.object({
  method: costForecastMethodSchema,
  /** True when no forecast was issued and the system computed one (FR-020). */
  derived: z.boolean(),
  asOf: isoDateSchema.nullable(),
  stale: z.boolean(),
  /** The issued version this came from, or null when it was derived. */
  versionId: z.string().uuid().nullable(),
  label: z.string().nullable(),
});
export type CostForecastState = z.infer<typeof costForecastStateSchema>;

export const costSummarySchema = z.object({
  projectId: z.string().uuid(),
  currency: currencyCodeSchema,
  asOf: timestampSchema,

  currentBudgetMinor: minorAmountSchema,
  originalBudgetMinor: minorAmountSchema,
  approvedChangesMinor: minorAmountSchema,
  contingencyMinor: minorAmountSchema,

  realizedMinor: minorAmountSchema,
  openCommitmentMinor: minorAmountSchema,
  committedTotalMinor: minorAmountSchema,
  cashMinor: minorAmountSchema,

  estimateToCompleteMinor: minorAmountSchema,
  anticipatedFinalCostMinor: minorAmountSchema,
  varianceMinor: minorAmountSchema,
  /** Realized as a share of the current budget. Null when there is no budget. */
  budgetUsedPercent: z.number().nullable(),

  forecast: costForecastStateSchema,
  freshness: z.array(costFreshnessSchema),
  warnings: z.array(costWarningSchema),
  hasBudget: z.boolean(),
  entryCount: z.number().int().nonnegative(),
});
export type CostSummary = z.infer<typeof costSummarySchema>;

/**
 * The amounts a reader can put on a cost tile.
 *
 * Every member is a field of the summary above with its `Minor` suffix removed,
 * which is the whole point: `costSummaryAmount` indexes the payload with a
 * template literal type, so a figure named here that no summary field backs
 * fails to compile. A configuration vocabulary and the shape it reads are the
 * classic pair that drifts, and this is the cheapest way to make drifting
 * impossible rather than merely discouraged.
 *
 * Ordered as a cost report reads: what was agreed, what has happened, where it
 * is going to end up.
 */
export const COST_SUMMARY_FIGURES = [
  'originalBudget',
  'approvedChanges',
  'currentBudget',
  'contingency',
  'realized',
  'openCommitment',
  'committedTotal',
  'cash',
  'estimateToComplete',
  'anticipatedFinalCost',
  'variance',
] as const;
export const costSummaryFigureSchema = z.enum(COST_SUMMARY_FIGURES);
export type CostSummaryFigure = (typeof COST_SUMMARY_FIGURES)[number];

/**
 * One figure's amount, by name.
 *
 * The indexed access is the compile-time check described above; it is not a
 * convenience over `summary.currentBudgetMinor`.
 */
export function costSummaryAmount(summary: CostSummary, figure: CostSummaryFigure): MinorAmount {
  return summary[`${figure}Minor`];
}

/**
 * Whether more of a figure is good news, bad news, or neither.
 *
 * A tile that colours every movement green when it rises would call an overrun
 * a success. Only variance carries a direction the reader should see coloured:
 * the rest are magnitudes whose meaning depends on the figure beside them, and
 * tinting them would assert a judgement the number does not support.
 */
export function costFigureIsVariance(figure: CostSummaryFigure): boolean {
  return figure === 'variance';
}

export const costTimeseriesQuerySchema = z.object({
  accountId: z.string().uuid().optional(),
  from: periodSchema.optional(),
  to: periodSchema.optional(),
});
export type CostTimeseriesQuery = z.infer<typeof costTimeseriesQuerySchema>;

export const costTimeseriesPointSchema = z.object({
  period: periodSchema,
  plannedMinor: minorAmountSchema,
  plannedCumulativeMinor: minorAmountSchema,
  committedMinor: minorAmountSchema,
  committedCumulativeMinor: minorAmountSchema,
  realizedMinor: minorAmountSchema,
  realizedCumulativeMinor: minorAmountSchema,
  /**
   * Null up to and including the current period: before today the realized
   * line is the truth, and drawing a forecast over it would imply a
   * distinction that does not exist.
   */
  forecastCumulativeMinor: minorAmountSchema.nullable(),
});
export type CostTimeseriesPoint = z.infer<typeof costTimeseriesPointSchema>;

/**
 * Earned value: what the spend actually bought
 * (docs/schedule-progress-design.md section 11).
 *
 * The cost page could say "we have spent 60% of the budget" and could not say
 * whether that 60% bought anything. With a percentage on tasks and budget lines
 * naming those tasks, it can: earned value is the budget of the work that has
 * been done, and the two ratios below are what it is for.
 *
 * ## Why coverage is not optional
 *
 * Only budget lines that name a schedule task can be earned against. A figure
 * computed over 70% of the budget and presented without saying so quietly
 * excludes a third of the project — so `coveragePercent` travels with the
 * numbers, always, and below `EARNED_VALUE_MIN_COVERAGE` the ratios are null
 * and the coverage is the only thing shown. A confident SPI over a fifth of the
 * project is worse than no SPI.
 *
 * ## What each number is measured over
 *
 * `earnedValueMinor` and `plannedValueMinor` are both taken over exactly the
 * linked lines, which is what makes SPI sound: the same scope on both sides of
 * the ratio.
 *
 * `actualCostMinor` is held to the same scope, which took some doing. Cost
 * entries name an *account*, not a budget line — tagging every invoice with a
 * work package is data entry nobody sustains, which is the reason the schedule
 * link sits on the line (`cost-monitoring-requirements.md` 2.12). So an
 * account's ledger is split across the tasks holding budget on it, in
 * proportion to how much each holds (`attributeCosts` in `@dashboard/costing`,
 * the same rule the schedule's own `cost` progress uses). It is exact wherever
 * an account's budget belongs to one task, and a proportion where it is
 * genuinely shared; `costScopeExact` says which case this project is in rather
 * than leaving a reader to assume the favourable one.
 *
 * It used to be the whole ledger of every account a linked line sat on, which
 * charged each task for its neighbours' invoices — and since CPI is a ratio, a
 * denominator carrying work the numerator does not read as an overrun that was
 * never there.
 */
export const costEarnedValueSchema = z.object({
  projectId: z.string().uuid(),
  currency: currencyCodeSchema,
  /** The date the figures were taken as of. */
  asOf: isoDateSchema,

  /** Budget of the linked lines, and of the project, in minor units. */
  linkedBudgetMinor: minorAmountSchema,
  totalBudgetMinor: minorAmountSchema,
  /** `linkedBudgetMinor` as a percentage of `totalBudgetMinor`, 0..100. */
  coveragePercent: z.number().int().min(0).max(100),

  /** The budget of the work that has been done. */
  earnedValueMinor: minorAmountSchema,
  /** What the plan said would have been spent by now, over the same lines. */
  plannedValueMinor: minorAmountSchema,
  /** Realized cost attributed to the linked tasks. */
  actualCostMinor: minorAmountSchema,
  /** Whether that needed no apportioning, making the figure above exact. */
  costScopeExact: z.boolean(),

  /**
   * `EV / PV` and `EV / AC`, as ratios rather than percentages: 1.0 is on plan,
   * below it is behind or over. Null when coverage is too thin to support them,
   * and null rather than infinity when the denominator is zero.
   */
  schedulePerformanceIndex: z.number().nullable(),
  costPerformanceIndex: z.number().nullable(),
  /**
   * `budget / CPI` — what the project would cost at the efficiency achieved so
   * far. Null whenever CPI is.
   */
  estimateAtCompletionMinor: minorAmountSchema.nullable(),
});
export type CostEarnedValue = z.infer<typeof costEarnedValueSchema>;

/**
 * Below this, the ratios are withheld and the coverage is shown instead.
 *
 * Half the budget is a judgement, not a law, and it is the number to argue
 * about rather than the presence of a floor: an SPI over a fifth of a project
 * is a number that will be quoted in a meeting and cannot survive being asked
 * where it came from.
 */
export const EARNED_VALUE_MIN_COVERAGE = 50;

export const costTimeseriesSchema = z.object({
  projectId: z.string().uuid(),
  currency: currencyCodeSchema,
  /** The period containing today, so the client can draw the "now" rule. */
  currentPeriod: periodSchema,
  budgetAtCompletionMinor: minorAmountSchema,
  points: z.array(costTimeseriesPointSchema),
});
export type CostTimeseries = z.infer<typeof costTimeseriesSchema>;

/**
 * The breakdown is deliberately not windowed by period.
 *
 * A budget is a whole-project commitment with no date of its own, so comparing
 * it against a windowed actual would put "the budget" beside "three months of
 * spend" and call the difference a variance. Narrowing the range belongs to the
 * time series, where both curves are windowed together.
 */
export const costBreakdownQuerySchema = z.object({
  parentAccountId: z.string().uuid().optional(),
});
export type CostBreakdownQuery = z.infer<typeof costBreakdownQuerySchema>;

/** Over/under is a state, so it is named rather than inferred from a sign. */
export const costVarianceStatusSchema = z.enum(['under', 'on_budget', 'over', 'no_budget']);
export type CostVarianceStatus = z.infer<typeof costVarianceStatusSchema>;

export const costBreakdownRowSchema = z.object({
  accountId: z.string().uuid(),
  /** Null for a row at the top of the returned slice. */
  parentId: z.string().uuid().nullable(),
  /** Depth relative to the slice root, so the client can indent without the tree. */
  depth: z.number().int().nonnegative(),
  code: costAccountCodeSchema,
  name: z.string(),
  kind: costAccountKindSchema,
  hasChildren: z.boolean(),
  budgetMinor: minorAmountSchema,
  realizedMinor: minorAmountSchema,
  openCommitmentMinor: minorAmountSchema,
  forecastMinor: minorAmountSchema,
  varianceMinor: minorAmountSchema,
  status: costVarianceStatusSchema,
});
export type CostBreakdownRow = z.infer<typeof costBreakdownRowSchema>;

/**
 * The whole subtree, not one level of it.
 *
 * An earlier version returned only the children of `parentAccountId`, which
 * made every level below the first reachable solely by navigating into it. On a
 * three-level chart of accounts that hid almost everything behind clicks, and
 * the totals a reader wants to compare were never on screen together. The rows
 * are already computed for every account when the breakdown is built, so
 * returning them all costs nothing and lets the client expand in place.
 *
 * Rows arrive depth-first with siblings ordered worst-variance-first, so the
 * order is meaningful whether or not the client re-sorts.
 */
export const costBreakdownSchema = z.object({
  projectId: z.string().uuid(),
  currency: currencyCodeSchema,
  parentAccountId: z.string().uuid().nullable(),
  rows: z.array(costBreakdownRowSchema),
});
export type CostBreakdown = z.infer<typeof costBreakdownSchema>;

/** The waterfall from the original budget to the anticipated final cost. */
export const costWaterfallStepSchema = z.object({
  key: z.enum([
    'original_budget',
    'approved_changes',
    'current_budget',
    'realized',
    'open_commitment',
    'estimate_to_complete',
    'anticipated_final_cost',
  ]),
  amountMinor: minorAmountSchema,
  /** Totals are drawn from the baseline; movements are drawn from the running sum. */
  isTotal: z.boolean(),
});
export type CostWaterfallStep = z.infer<typeof costWaterfallStepSchema>;

export const costWaterfallSchema = z.object({
  projectId: z.string().uuid(),
  currency: currencyCodeSchema,
  steps: z.array(costWaterfallStepSchema),
});
export type CostWaterfall = z.infer<typeof costWaterfallSchema>;

/** One batched read for the whole overview page (requirements document 12.2). */
export const costOverviewSchema = z.object({
  summary: costSummarySchema,
  timeseries: costTimeseriesSchema,
  breakdown: costBreakdownSchema,
  waterfall: costWaterfallSchema,
  /**
   * What this reader may author. Travels with the page it governs, so the
   * controls and the figures are always from the same snapshot.
   */
  editability: costEditabilitySchema,
});
export type CostOverview = z.infer<typeof costOverviewSchema>;

/* -------------------------------------------------------------------------- */
/* Cost sources and their claims                                               */
/* -------------------------------------------------------------------------- */

/**
 * A claim is how a source says "these accounts are mine".
 *
 * Per domain, because the realistic configuration is mixed: an ERP masters
 * invoices while the project team still writes its own budget. Over a subtree,
 * because a chart of accounts is a hierarchy and an external system owns a
 * branch of it (docs/cost-editing-design.md, section 4).
 */
export const costSourceClaimSchema = z.object({
  id: z.string().uuid(),
  sourceId: z.string().uuid(),
  costAccountId: z.string().uuid(),
  domain: costDomainSchema,
  includeDescendants: z.boolean(),
  createdAt: timestampSchema,
});
export type CostSourceClaim = z.infer<typeof costSourceClaimSchema>;

export const createCostSourceClaimSchema = z
  .object({
    costAccountId: z.string().uuid(),
    domain: costDomainSchema,
    includeDescendants: z.boolean().default(true),
    /**
     * Claiming a subtree that already holds manually authored entries is
     * refused unless this is set, and then it raises a standing data-quality
     * warning naming them. Silently absorbing them is how the same invoice
     * comes to be counted twice (requirements document 2.9).
     */
    acknowledgeManualEntries: z.boolean().default(false),
  })
  .strict();
export type CreateCostSourceClaim = z.infer<typeof createCostSourceClaimSchema>;

export const costSourceSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  mode: costSourceModeSchema,
  /** Set for `mode: 'synced'`: the integration the sync job pulls from. */
  integrationId: z.string().uuid().nullable(),
  suspenseAccountId: z.string().uuid().nullable(),
  lastSuccessAt: timestampSchema.nullable(),
  consecutiveFailures: z.number().int().nonnegative(),
  lastErrorCode: z.string().nullable(),
  claims: z.array(costSourceClaimSchema),
  /** Entries attributed to this source. Deleting one with history is refused. */
  entryCount: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type CostSource = z.infer<typeof costSourceSchema>;

export const createCostSourceSchema = z
  .object({
    name: z.string().min(1).max(120),
    mode: costSourceModeSchema.default('internal'),
    /** Required when `mode: 'synced'`; the service checks the pairing (a
     *  cost-capable provider, not already attached elsewhere). */
    integrationId: z.string().uuid().nullish(),
    suspenseAccountId: z.string().uuid().nullish(),
  })
  .strict();
export type CreateCostSource = z.infer<typeof createCostSourceSchema>;

export const updateCostSourceSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    mode: costSourceModeSchema.optional(),
    integrationId: z.string().uuid().nullish(),
    suspenseAccountId: z.string().uuid().nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateCostSource = z.infer<typeof updateCostSourceSchema>;

/* -------------------------------------------------------------------------- */
/* Forecast versions                                                           */
/* -------------------------------------------------------------------------- */

/**
 * A forecast is the same kind of object as a budget: a sequence of statements
 * somebody stood behind, immutable once issued (requirements document 5.5).
 * Until one is issued the system computes one and says so -- which is what
 * `CostForecastState.derived` has meant since the first release.
 */
export const costForecastStatusSchema = z.enum(['draft', 'issued', 'superseded']);
export type CostForecastStatus = z.infer<typeof costForecastStatusSchema>;

export const costForecastLineSchema = z.object({
  costAccountId: z.string().uuid(),
  period: periodSchema,
  amountMinor: minorAmountSchema,
});
export type CostForecastLine = z.infer<typeof costForecastLineSchema>;

export const costForecastVersionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  sequence: z.number().int().positive(),
  label: z.string(),
  status: costForecastStatusSchema,
  method: costForecastMethodSchema,
  asOf: isoDateSchema,
  note: z.string().nullable(),
  /** The estimate to complete this version states, summed over its lines. */
  estimateToCompleteMinor: minorAmountSchema,
  lineCount: z.number().int().nonnegative(),
  issuedByUserId: z.string().uuid().nullable(),
  issuedAt: timestampSchema.nullable(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type CostForecastVersion = z.infer<typeof costForecastVersionSchema>;

export const createCostForecastVersionSchema = z
  .object({
    label: z.string().min(1).max(200),
    /**
     * The method the draft starts from. `manual` opens an empty sheet; any
     * other seeds the draft with what that method computes right now, which is
     * the difference between a forecast somebody adjusts and a forecast
     * somebody has to type from nothing.
     */
    method: costForecastMethodSchema.default('budget_remaining'),
    asOf: isoDateSchema.nullish(),
    note: z.string().max(2000).nullish(),
  })
  .strict();
export type CreateCostForecastVersion = z.infer<typeof createCostForecastVersionSchema>;

export const updateCostForecastVersionSchema = z
  .object({
    label: z.string().min(1).max(200).optional(),
    method: costForecastMethodSchema.optional(),
    asOf: isoDateSchema.optional(),
    note: z.string().max(2000).nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateCostForecastVersion = z.infer<typeof updateCostForecastVersionSchema>;

/**
 * Lines are replaced as a set, never edited one at a time.
 *
 * A forecast is a statement about every account and period at once; a
 * line-at-a-time API would let a draft be half of one forecast and half of
 * another, with no moment at which it was ever a whole one.
 */
export const replaceCostForecastLinesSchema = z
  .object({
    lines: z.array(costForecastLineSchema).max(5000),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type ReplaceCostForecastLines = z.infer<typeof replaceCostForecastLinesSchema>;

export const issueCostForecastVersionSchema = z
  .object({
    note: z.string().max(2000).nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type IssueCostForecastVersion = z.infer<typeof issueCostForecastVersionSchema>;
